import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  MessageSquare,
  Users,
  BrainCircuit,
  Shield,
  ClipboardList,
} from "lucide-react";

const AshaOperatingSystemSlide = () => {
  const colorMap = {
    blue: { accent: "text-blue-400", border: "border-blue-500/20", bg: "bg-blue-500/5" },
    purple: { accent: "text-purple-400", border: "border-purple-500/20", bg: "bg-purple-500/5" },
    emerald: { accent: "text-emerald-400", border: "border-emerald-500/20", bg: "bg-emerald-500/5" },
    cyan: { accent: "text-cyan-400", border: "border-cyan-500/20", bg: "bg-cyan-500/5" },
  };

  const sections = [
    {
      icon: Users,
      title: "Front Office AI Products",
      color: "blue",
      items: [
        "Amy (Receptionist)",
        "Leena (Schedule Optimizer)",
        "Stephanie (Hygiene Recall)",
        "Elise (Patient Experience)",
        "Kim (Case Acceptance)",
        "Morgan (Patient Intake)",
        "Trisha (Practice Manager)",
        "Joseph (Fax Management)",
      ],
    },
    {
      icon: MessageSquare,
      title: "Communication Suite + Marketing Suite",
      color: "purple",
      items: [
        "VoIP Phone System",
        "Physical Desk Phones (Yealink)",
        "2-Way Texting",
        "Universal Inbox",
        "Team Chat & Channels",
        "Smart Voice Logs",
        "Automated Workflows",
        "Intelligent Fax",
        "Next-Gen Forms",
        "SMS & Email Campaigns",
        "Text-to-Pay Payments",
        "Payment Card Readers",
      ],
    },
    {
      icon: Shield,
      title: "Back Office AI Products + Human Biller",
      color: "emerald",
      items: [
        "Amanda (Verification)",
        "Ariel (Claim Submission)",
        "Alayna (Claim Scrubbing)",
        "Sarah (Denial Mgmt)",
        "Kianna (Patient Billing)",
        "Julia (AR & Collections)",
        "Alisha (EOB Posting)",
        "Laura (Insurance Calls)",
        "US-Based Expert",
        "AI-Certified Specialist",
        "Strategic Consulting",
      ],
    },
    {
      icon: ClipboardList,
      title: "PMS + Imaging",
      color: "cyan",
      items: [
        "Patient Charts",
        "Perio Charting",
        "Ledgers & Billing",
        "Treatment Planning",
        "Scheduling",
        "eClaims Built-In",
        "Cloud-Based — No Server",
        "Reporting & Analytics",
        "SOTA Cloud Imaging",
        "AI Copilot",
        "AI Scribe",
        "AI Treatment Planner",
      ],
    },
  ];

  const Section = ({ section, delay }) => {
    const c = colorMap[section.color];
    const Icon = section.icon;
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay }}
        className={`rounded-xl border ${c.border} ${c.bg} backdrop-blur-sm px-4 py-3 flex flex-col`}
      >
        <div className="flex items-center gap-2.5 mb-2.5 flex-shrink-0">
          <div
            className={`w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center ${c.accent} ring-1 ring-white/10`}
          >
            <Icon className="w-3.5 h-3.5" />
          </div>
          <h3 className="text-sm font-bold text-white leading-tight">
            {section.title}
          </h3>
        </div>
        <div className="space-y-0.5 overflow-hidden">
          {section.items.map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-[13px] font-medium text-slate-300"
            >
              <CheckCircle2
                className={`w-3.5 h-3.5 ${c.accent} opacity-70 shrink-0`}
              />
              <span className="truncate">{item}</span>
            </div>
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="h-full w-full bg-[#0B1120] text-white flex flex-col justify-center px-8 py-6 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-blue-600/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-indigo-600/10 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2 pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none" />

      {/* Header */}
      <div className="z-10 relative flex-shrink-0 mb-5">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-blue-500/10 to-indigo-500/10 text-blue-400 border border-blue-500/20 text-sm font-semibold mb-2 backdrop-blur-md">
            <BrainCircuit className="w-4 h-4" />
            <span className="tracking-wide">The Complete Platform</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-slate-400 leading-[1.05]">
            The Asha Dental AI OS
          </h1>
          <p className="text-xl text-slate-300 font-medium mt-2 mb-3">
            20+ AI agents + 10 Softwares in 1 platform.
          </p>

        </motion.div>
      </div>

      {/* Content Grid - Single Row, 4 Columns */}
      <div className="grid grid-cols-4 gap-3 z-10 max-w-[1600px] mx-auto w-full">
        {sections.map((section, i) => (
          <Section key={i} section={section} delay={i * 0.08} />
        ))}
      </div>
    </div>
  );
};

export default AshaOperatingSystemSlide;
